import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, mergeProps as _mergeProps, createBlock as _createBlock, toDisplayString as _toDisplayString, resolveDirective as _resolveDirective, withDirectives as _withDirectives, renderList as _renderList, normalizeClass as _normalizeClass, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "full-width full-height gallery-container" }
const _hoisted_2 = { class: "flex column no-wrap full-height" }
const _hoisted_3 = {
  class: "absolute q-mr-xs",
  style: {"right":"0","top":"50%","bottom":"unset","transform":"translateY(-50%)","z-index":"1000"}
}
const _hoisted_4 = {
  class: "absolute q-ml-xs",
  style: {"top":"50%","left":"0","bottom":"unset","transform":"translateY(-50%)","z-index":"1000"}
}
const _hoisted_5 = {
  key: 0,
  class: "q-px-sm gallery-image-description bg-black text-white absolute-bottom-left"
}
const _hoisted_6 = {
  class: "row no-wrap q-gutter-sm full-height",
  style: {"min-width":"max-content"}
}
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "fixed-top-right z-max q-pa-md header__fix" }

import {useDialogPluginComponent, useQuasar} from 'quasar'
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue'

interface GalleryImage {
  file: string
  description?: string
  default_alt_text?: string
  thumb?: string
}

const MIN_SCALE = 1
const MAX_SCALE = 5
const ZOOM_STEP = 0.25


export default /*@__PURE__*/_defineComponent({
  __name: 'GalleryModal',
  props: {
  images: {
    required: true,
    type: Array<GalleryImage>,
    default: () => []
  },
  position: {
    type: Number,
    default: 0
  },
},
  emits: [
  // REQUIRED; need to specify some events that your
  // component will emit through useDialogPluginComponent()
  ...useDialogPluginComponent.emits
],
  setup(__props) {

const $q = useQuasar()

const props = __props



const {onDialogHide, dialogRef} = useDialogPluginComponent()

// export interface Image {
//   file: string,
//   description?: string
//   default_alt_text?: string
//   thumb?: object
// }

// Zoom configuration
const scale = ref(1.0)
const activeImagePosition = ref(0)
const translateX = ref(0)
const translateY = ref(0)
const thumbContainer = ref<HTMLElement | null>(null)
const galleryThumbRefs = ref<Array<any>>([])
const activeImg = ref<any>(null)
const clickTimeout = ref<NodeJS.Timeout | null>(null)
const isPanning = ref(false)

// Touch/pinch handling variables
const touchStartDistance = ref(0)
const touchStartScale = ref(1)
const initialTouchCenter = ref({ x: 0, y: 0 })
const recentlyPinched = ref(false)

// Smart zoom and animation variables
const isAnimating = ref(false)
const currentZoomLevel = ref(0) // Track which zoom level we're at
const lastPinchTime = ref(0)
const pinchVelocity = ref(0)

function getImgComponent(image: any, thumbnail = false) {
  return {
    src: thumbnail ? image.thumb : image.file
  }
}

// Zoom and pan handlers
function handleWheel(e: WheelEvent) {
  e.preventDefault()
  const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP

  // Get cursor position relative to the image viewport
  const imageViewport = e.currentTarget as HTMLElement
  const rect = imageViewport.getBoundingClientRect()
  const cursorX = e.clientX - rect.left
  const cursorY = e.clientY - rect.top

  zoomAtPoint(scale.value + delta, cursorX, cursorY, rect.width, rect.height)
}

function handleTouchStart(e: TouchEvent) {
  if (e.touches.length === 2) {
    // Prevent default to avoid zoom/pan conflicts
    e.preventDefault()

    const touch1 = e.touches[0]
    const touch2 = e.touches[1]

    // Calculate initial distance between touches
    touchStartDistance.value = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    )

    // Store the current scale as baseline
    touchStartScale.value = scale.value

    // Track timing for velocity calculation
    lastPinchTime.value = Date.now()

    // Calculate center point of pinch
    initialTouchCenter.value = {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    }
  }
}

function handleTouchMove(e: TouchEvent) {
  if (e.touches.length === 2 && touchStartDistance.value > 0) {
    // Prevent default scrolling/zooming
    e.preventDefault()

    const touch1 = e.touches[0]
    const touch2 = e.touches[1]

    // Calculate current distance between touches
    const currentDistance = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    )

    // Calculate scale factor based on distance change
    const scaleChange = currentDistance / touchStartDistance.value
    let newScale = touchStartScale.value * scaleChange

    // Apply velocity-based scaling for more responsive feel
    const currentTime = Date.now()
    const timeDelta = currentTime - lastPinchTime.value
    if (timeDelta > 0) {
      const currentVelocity = Math.abs(scaleChange - 1) / timeDelta * 1000 // scale change per second
      pinchVelocity.value = currentVelocity

      // Fast pinch = more aggressive zoom, slow pinch = precise zoom
      if (currentVelocity > 2) {
        // Fast pinch: amplify the scale change
        const amplificationFactor = Math.min(1.5, 1 + currentVelocity * 0.1)
        newScale = touchStartScale.value + (newScale - touchStartScale.value) * amplificationFactor
      }
    }

    // Get current pinch center
    const currentTouchCenter = {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    }

    // Get image container for coordinate calculations
    const imageContainer = document.querySelector('.image-container') as HTMLElement
    if (imageContainer) {
      const rect = imageContainer.getBoundingClientRect()
      const pointX = currentTouchCenter.x - rect.left
      const pointY = currentTouchCenter.y - rect.top

      zoomAtPoint(newScale, pointX, pointY, rect.width, rect.height)
    }

    lastPinchTime.value = currentTime
  }
}

function handleTouchEnd(e: TouchEvent) {
  if (e.touches.length < 2) {
    // If we were pinching and now have fewer than 2 touches, mark as recently pinched
    if (touchStartDistance.value > 0) {
      recentlyPinched.value = true
      // Clear the flag after a short delay to allow pan gestures again
      setTimeout(() => {
        recentlyPinched.value = false
      }, 200)
    }

    // Reset pinch tracking when fewer than 2 touches
    touchStartDistance.value = 0
    touchStartScale.value = 1
    initialTouchCenter.value = { x: 0, y: 0 }
  }
}

function zoomAtPoint(newScale: number, pointX: number, pointY: number, containerWidth: number, containerHeight: number) {
  const prevScale = scale.value
  const clampedScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale))

  if (clampedScale === prevScale) return

  if (clampedScale === MIN_SCALE) {
    resetImageTransformations()
    return
  }

  // Simple zoom-to-point calculation:
  // The point should remain stationary during zoom
  const scaleDelta = clampedScale - prevScale
  const centerX = containerWidth / 2
  const centerY = containerHeight / 2

  // Distance from center to zoom point
  const offsetX = pointX - centerX
  const offsetY = pointY - centerY

  // Adjust translation to keep point under cursor
  translateX.value -= (offsetX * scaleDelta) / clampedScale
  translateY.value -= (offsetY * scaleDelta) / clampedScale

  scale.value = clampedScale
  constrainPanning()
}

function handlePan(ev: any) {
  if (scale.value <= MIN_SCALE) return

  // Prevent panning immediately after pinching to avoid jumps
  if (recentlyPinched.value) return

  isPanning.value = true

  // Adjust pan sensitivity based on scale - higher scale = less sensitive
  const panSensitivity = 1 / scale.value
  translateX.value += ev.delta.x * panSensitivity
  translateY.value += ev.delta.y * panSensitivity

  constrainPanning()

  // Reset panning state after a delay
  setTimeout(() => {
    isPanning.value = false
  }, 100)
}

function constrainPanning() {
  if (!activeImg.value?.$el) return

  const imageContainer = document.querySelector('.image-container') as HTMLElement
  if (!imageContainer) return

  const containerWidth = imageContainer.clientWidth
  const containerHeight = imageContainer.clientHeight

  // Get the actual rendered image dimensions
  const imgElement = activeImg.value.$el.querySelector('img')
  if (!imgElement) return

  const imageWidth = imgElement.naturalWidth || imgElement.width
  const imageHeight = imgElement.naturalHeight || imgElement.height

  // Calculate the display size of the image when fit="contain"
  const containerAspect = containerWidth / containerHeight
  const imageAspect = imageWidth / imageHeight

  let displayWidth, displayHeight

  if (imageAspect > containerAspect) {
    // Image is wider than container - width constrained
    displayWidth = containerWidth
    displayHeight = containerWidth / imageAspect
  } else {
    // Image is taller than container - height constrained
    displayHeight = containerHeight
    displayWidth = containerHeight * imageAspect
  }

  // Calculate maximum translation based on scaled image size
  const scaledImageWidth = displayWidth * scale.value
  const scaledImageHeight = displayHeight * scale.value

  // Maximum translation is when the image edge reaches the container edge
  const maxTranslateX = Math.max(0, (scaledImageWidth - containerWidth) / 2)
  const maxTranslateY = Math.max(0, (scaledImageHeight - containerHeight) / 2)

  translateX.value = Math.max(-maxTranslateX, Math.min(maxTranslateX, translateX.value))
  translateY.value = Math.max(-maxTranslateY, Math.min(maxTranslateY, translateY.value))
}

function resetZoom() {
  resetImageTransformations()
}

// Smooth zoom animation function
function animateToScale(targetScale: number, centerX?: number, centerY?: number) {
  if (isAnimating.value) return

  isAnimating.value = true
  const startScale = scale.value
  const startTranslateX = translateX.value
  const startTranslateY = translateY.value

  // If center point provided, calculate target translation
  let targetTranslateX = startTranslateX
  let targetTranslateY = startTranslateY

  if (centerX !== undefined && centerY !== undefined) {
    const imageContainer = document.querySelector('.image-container') as HTMLElement
    if (imageContainer) {
      const containerWidth = imageContainer.clientWidth
      const containerHeight = imageContainer.clientHeight
      const scaleDelta = targetScale - startScale
      const offsetX = centerX - containerWidth / 2
      const offsetY = centerY - containerHeight / 2

      targetTranslateX = startTranslateX - (offsetX * scaleDelta) / targetScale
      targetTranslateY = startTranslateY - (offsetY * scaleDelta) / targetScale
    }
  }

  // Reset to center if zooming to minimum scale
  if (targetScale === MIN_SCALE) {
    targetTranslateX = 0
    targetTranslateY = 0
  }

  // Set target values and let CSS transition handle the animation
  scale.value = targetScale
  translateX.value = targetTranslateX
  translateY.value = targetTranslateY

  // Constrain and finish animation
  setTimeout(() => {
    constrainPanning()
    isAnimating.value = false
  }, 300)
}

// Smart double-tap with cycling through zoom levels
function handleSmartDoubleTap(centerX: number, centerY: number) {
  const levels = smartZoomLevels.value
  const currentScale = scale.value

  // Find the next zoom level
  let nextLevelIndex = 0
  for (let i = 0; i < levels.length; i++) {
    if (currentScale < levels[i] - 0.1) { // Small tolerance for floating point comparison
      nextLevelIndex = i
      break
    }
  }

  // If we're at the highest level, go back to minimum
  if (nextLevelIndex === 0 && currentScale >= levels[levels.length - 1] - 0.1) {
    nextLevelIndex = 0
    currentZoomLevel.value = 0
  } else {
    currentZoomLevel.value = nextLevelIndex
  }

  const targetScale = levels[nextLevelIndex]
  animateToScale(targetScale, centerX, centerY)
}

function handleImageClick(e: MouseEvent) {
  if (isPanning.value || isAnimating.value) return

  if (clickTimeout.value) {
    clearTimeout(clickTimeout.value)
    clickTimeout.value = null
    return
  }

  const target = e.currentTarget as HTMLElement
  if (!target) return

  const rect = target.getBoundingClientRect()
  const clickX = e.clientX - rect.left
  const clickY = e.clientY - rect.top

  clickTimeout.value = setTimeout(() => {
    // Use smart double-tap instead of simple zoom toggle
    handleSmartDoubleTap(clickX, clickY)
    clickTimeout.value = null
  }, 250)
}

function keydownHandler(e: KeyboardEvent) {
  if (e.key === 'ArrowLeft') {
    activeImagePosition.value = getPrevPosition(activeImagePosition.value)
  }
  if (e.key === 'ArrowRight') {
    activeImagePosition.value = getNextPosition(activeImagePosition.value)
  }
}

function scrollToThumb(pos: number) {
  if (!galleryThumbRefs.value.length || galleryThumbRefs.value.length <= pos) return
  const thumbElement = galleryThumbRefs.value[pos]
  if (!thumbElement || !thumbContainer.value) return

  const containerWidth = thumbContainer.value.clientWidth
  const thumbWidth = thumbElement.offsetWidth
  const thumbLeft = thumbElement.offsetLeft

  // Calculate the position to center the thumbnail in view
  const scrollPosition = thumbLeft - (containerWidth / 2) + (thumbWidth / 2)

  // Smooth scroll to the calculated position
  thumbContainer.value.scrollTo({
    left: scrollPosition,
    behavior: 'smooth'
  })
}

function handleSwipe({ direction }: { direction: string }) {
  if (direction === 'right') {
    activeImagePosition.value = getPrevPosition(activeImagePosition.value)
  }
  if (direction === 'left') {
    activeImagePosition.value = getNextPosition(activeImagePosition.value)
  }
}

function resetImageTransformations() {
  // Use smooth animation when resetting
  if (scale.value !== MIN_SCALE || translateX.value !== 0 || translateY.value !== 0) {
    animateToScale(MIN_SCALE)
  } else {
    // Instant reset if already at default state
    scale.value = MIN_SCALE
    translateX.value = 0
    translateY.value = 0
    isPanning.value = false
    currentZoomLevel.value = 0
  }
}

function getNextPosition(pos: number): number {
  const newPos = pos + 1
  return newPos > props.images.length - 1 ? 0 : newPos
}

function getPrevPosition(pos: number): number {
  const newPos = pos - 1
  return newPos < 0 ? props.images.length - 1 : newPos
}

const transformStyle = computed(() => {
  return {
    transform: `scale(${scale.value}) translateX(${translateX.value}px) translateY(${translateY.value}px)`,
    transition: isAnimating.value ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none'
  }
})

// Smart zoom levels: fit to screen, 1:1 pixel ratio, fill screen, max zoom
const smartZoomLevels = computed(() => {
  if (!activeImg.value?.$el) return [MIN_SCALE, 2, 3, MAX_SCALE]

  const imageContainer = document.querySelector('.image-container') as HTMLElement
  if (!imageContainer) return [MIN_SCALE, 2, 3, MAX_SCALE]

  const containerWidth = imageContainer.clientWidth
  const containerHeight = imageContainer.clientHeight
  const imgElement = activeImg.value.$el.querySelector('img')

  if (!imgElement) return [MIN_SCALE, 2, 3, MAX_SCALE]

  const imageWidth = imgElement.naturalWidth || imgElement.width
  const imageHeight = imgElement.naturalHeight || imgElement.height

  // Calculate meaningful zoom levels
  const fitScale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight)
  const fillScale = Math.max(containerWidth / imageWidth, containerHeight / imageHeight)
  const pixelPerfectScale = 1.0 // 1:1 pixel ratio

  return [
    MIN_SCALE,                    // Level 0: Fit to container (default)
    Math.max(fitScale, 1.5),     // Level 1: Better fit or 1.5x minimum
    pixelPerfectScale,           // Level 2: 1:1 pixel ratio
    Math.max(fillScale, 3),      // Level 3: Fill container or 3x minimum
    MAX_SCALE                    // Level 4: Maximum zoom
  ].sort((a, b) => a - b) // Ensure ascending order
})

const activeImage = computed(() => {
  if (!props.images.length) return undefined
  return props.images[activeImagePosition.value] || props.images[0]
})

onMounted(() => {
  activeImagePosition.value = Math.max(0, Math.min(props.position, props.images.length - 1))
  document.addEventListener('keydown', keydownHandler)
  document.body.style.overflow = 'hidden'
})

onUnmounted(() => {
  document.removeEventListener('keydown', keydownHandler)
  if (clickTimeout.value) {
    clearTimeout(clickTimeout.value)
  }
  document.body.style.overflow = ''
})

watch(
  () => activeImagePosition.value,
  async (newVal) => {
    resetImageTransformations()
    // Use nextTick to ensure DOM has updated before scrolling
    await nextTick()
    setTimeout(() => {
      scrollToThumb(newVal)
    }, 50)
  },
  { immediate: true }
)


return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_img = _resolveComponent("q-img")!
  const _component_q_dialog = _resolveComponent("q-dialog")!
  const _directive_touch_pan = _resolveDirective("touch-pan")!
  const _directive_touch_swipe = _resolveDirective("touch-swipe")!
  const _directive_close_popup = _resolveDirective("close-popup")!

  return (_openBlock(), _createBlock(_component_q_dialog, {
    ref_key: "dialogRef",
    ref: dialogRef,
    maximized: "",
    onHide: _unref(onDialogHide)
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _withDirectives((_openBlock(), _createElementBlock("div", {
            class: "image-container text-center relative full-height flex flex-center",
            style: {"flex":"1","user-select":"none","min-height":"0"},
            onWheel: handleWheel
          }, [
            (_unref($q).screen.gt.xs)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("div", _hoisted_3, [
                    _createVNode(_component_q_btn, {
                      color: "black",
                      "text-color": "white",
                      round: "",
                      icon: "navigate_next",
                      onClick: _cache[0] || (_cache[0] = ($event: any) => {activeImagePosition.value = getNextPosition(activeImagePosition.value); resetImageTransformations()})
                    })
                  ]),
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_component_q_btn, {
                      color: "black",
                      "text-color": "white",
                      round: "",
                      icon: "navigate_before",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => {activeImagePosition.value = getPrevPosition(activeImagePosition.value); resetImageTransformations()})
                    })
                  ])
                ], 64))
              : _createCommentVNode("", true),
            (activeImage.value)
              ? _withDirectives((_openBlock(), _createElementBlock("div", {
                  key: activeImage.value.file,
                  class: "relative-position c-active-image",
                  onDblclick: resetZoom,
                  onTouchstart: handleTouchStart,
                  onTouchmove: handleTouchMove,
                  onTouchend: handleTouchEnd
                }, [
                  (_openBlock(), _createBlock(_component_q_img, _mergeProps(getImgComponent(activeImage.value), {
                    key: activeImagePosition.value,
                    ref_key: "activeImg",
                    ref: activeImg,
                    draggable: false,
                    class: ["zoom-image", {
                  'zoom-out-cursor': scale.value > MIN_SCALE && !isPanning.value,
                  'grabbing-cursor': isPanning.value
                }],
                    fit: "contain",
                    style: [{"width":"100%","height":"100%","transform-origin":"center center"}, transformStyle.value],
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (handleImageClick($event)))
                  }), null, 16, ["class", "style"])),
                  (activeImage.value.description)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(activeImage.value.description), 1))
                    : _createCommentVNode("", true)
                ], 32)), [
                  [
                    _directive_touch_pan,
                    scale.value > MIN_SCALE && !isAnimating.value ? handlePan : undefined,
                    void 0,
                    { mouse: true }
                  ]
                ])
              : _createCommentVNode("", true)
          ], 32)), [
            [
              _directive_touch_swipe,
              scale.value === MIN_SCALE ? handleSwipe : undefined,
              void 0,
              { mouse: true }
            ]
          ]),
          _createElementVNode("div", {
            ref_key: "thumbContainer",
            ref: thumbContainer,
            class: "thumbnail-container"
          }, [
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.images, (obj, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: obj.file,
                  ref_for: true,
                  ref: el => galleryThumbRefs.value[index] = el,
                  class: _normalizeClass(["thumbnails", {'active': index === activeImagePosition.value}]),
                  onClick: ($event: any) => {activeImagePosition.value = index; resetImageTransformations()}
                }, [
                  _createVNode(_component_q_img, _mergeProps({ ref_for: true }, getImgComponent(obj, true), {
                    ratio: "16/9",
                    class: "cursor-pointer",
                    style: {"width":"120px","height":"100%"},
                    fit: "cover"
                  }), null, 16)
                ], 10, _hoisted_7))
              }), 128))
            ])
          ], 512)
        ]),
        _createElementVNode("div", _hoisted_8, [
          _withDirectives(_createVNode(_component_q_btn, {
            color: "white",
            "text-color": "dark",
            round: "",
            dense: "",
            size: "lg",
            icon: "close"
          }, null, 512), [
            [_directive_close_popup]
          ])
        ])
      ])
    ]),
    _: 1
  }, 8, ["onHide"]))
}
}

})